'use client';

import { type CSSProperties, ComponentType, ReactNode, useId } from 'react';
import Link from 'next/link';
import { motion, useReducedMotion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
export interface RailItem {
  id: string;
  icon: ComponentType<{
    size?: number;
    className?: string;
  }>;
  label: string;
  /** Tooltip subtitle — e.g. registry `navDescription`. */
  description?: string;
  href?: string;
  accentColor?: string;
  disabled?: boolean;
}
interface RailProps {
  items: RailItem[];
  activeId?: string;
  onActivate?: (id: string) => void;
  variant?: 'persona' | 'nav';
  topSlot?: ReactNode;
  bottomSlot?: ReactNode;
  width?: number;
  ariaLabel?: string;
  className?: string;
  /** Show label tooltips on hover/focus. Defaults to true for persona rails. */
  showTooltips?: boolean;
}
const DEFAULT_WIDTH = 72;

/** Far-left mission rail strip width — keep in sync with header logo column. */
export const RAIL_STRIP_WIDTH = DEFAULT_WIDTH;
export function Rail({
  items,
  activeId,
  onActivate,
  variant = 'persona',
  topSlot,
  bottomSlot,
  width = DEFAULT_WIDTH,
  ariaLabel = 'Primary navigation',
  className,
  showTooltips = variant === 'persona'
}: RailProps) {
  const layoutId = useId();
  const reduceMotion = useReducedMotion();
  const renderItem = (item: RailItem) => {
    const Icon = item.icon;
    const isActive = activeId === item.id;
    const itemAccent = variant === 'persona' ? item.accentColor ?? 'var(--accent-c)' : 'var(--accent-c)';
    const inactiveStyle = !isActive && !item.disabled ? {
      '--rail-accent': itemAccent
    } as CSSProperties : undefined;
    const className_0 = cn('relative flex items-center justify-center w-11 h-11 rounded-md', 'transition-colors duration-quick', !item.disabled && !isActive && ['text-tertiary hover:bg-surface-2 cursor-pointer', 'hover:text-[var(--rail-accent)]', 'focus-visible:outline-none focus-visible:ring-2', 'focus-visible:ring-[var(--rail-accent)] focus-visible:ring-offset-2', 'focus-visible:ring-offset-surface-1'], item.disabled && 'opacity-40 cursor-not-allowed');
    const inner = <>
        {isActive && <motion.span aria-hidden="true" layoutId={`${layoutId}-active`} className="absolute inset-0 rounded-md bg-surface-3" transition={reduceMotion ? {
        duration: 0
      } : {
        type: 'spring',
        stiffness: 380,
        damping: 30,
        mass: 0.8
      }} />}
        <Icon size={20} aria-hidden="true" className="relative z-10" />
      </>;
    const controlProps = {
      'aria-label': item.label,
      'aria-current': isActive ? item.href ? 'page' as const : 'true' as const : undefined,
      className: className_0,
      style: isActive ? {
        color: itemAccent
      } : inactiveStyle
    };
    const tooltipLabel = item.description ? `${item.label} — ${item.description}` : item.label;
    const control = item.href ? <Link href={item.href} {...controlProps}>
        {inner}
      </Link> : <button type="button" onClick={() => onActivate?.(item.id)} disabled={item.disabled} {...controlProps}>
        {inner}
      </button>;
    if (!showTooltips) {
      return item.href ? <Link key={item.id} href={item.href} {...controlProps}>
          {inner}
        </Link> : <button key={item.id} type="button" onClick={() => onActivate?.(item.id)} disabled={item.disabled} {...controlProps}>
          {inner}
        </button>;
    }
    return <Tooltip key={item.id}>
        <TooltipTrigger asChild>{control}</TooltipTrigger>
        <TooltipContent side="right" sideOffset={6} className="max-w-xs">
          {tooltipLabel}
        </TooltipContent>
      </Tooltip>;
  };
  return <nav aria-label={ariaLabel} className={cn('flex flex-col items-center shrink-0 h-full', className)} style={{
    width
  }}>
      {topSlot && <div className="flex items-center justify-center shrink-0 py-3 w-full">
          {topSlot}
        </div>}

      <div role="group" aria-label={`${ariaLabel} items`} className="flex flex-col items-center gap-1 py-3 flex-1">
        {items.map(renderItem)}
      </div>

      {bottomSlot && <div className="flex flex-col items-center gap-1 pb-3 shrink-0 w-full">
          {bottomSlot}
        </div>}
    </nav>;
}